/**
 * Admin gate around the oversight panels. Renders ONLY for users the server
 * confirmed as admin (`useAuth().isAdmin`). Non-admins see an access-denied message.
 *
 * AUTHZ NOTE: the Worker is the authoritative security boundary. The frontend
 * isAdmin gate is defense-in-depth only — the Worker independently enforces the
 * admin claim on every request.
 *
 * Tabs for Fase 0: AI providers, Users, Audit.
 * SKIP: AgentPanel, TelegramPanel, WelcomePanel, ModulesPanel, AdminConfigPanel,
 *       CampaniasPanel — not in scope for Fase 0.
 */

import { useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext.js";
import { AIPanel } from "./AIPanel.js";
import { UsersPanel } from "./UsersPanel.js";
import { AuditPanel } from "./AuditPanel.js";
import { KnowledgePanel } from "./KnowledgePanel.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import type { AdminHooksDeps } from "./hooks.js";

export interface AdminPanelProps {
  workerUrl: string;
}

type AdminTab = "ai" | "users" | "audit" | "knowledge";

const TABS: ReadonlyArray<{ id: AdminTab; label: string }> = [
  { id: "ai", label: "Proveedores IA" },
  { id: "users", label: "Usuarios" },
  { id: "audit", label: "Auditoría" },
  { id: "knowledge", label: "Conocimiento" },
];

export function AdminPanel({ workerUrl }: AdminPanelProps): JSX.Element {
  const { isAdmin, getIdToken } = useAuth();
  const [tab, setTab] = useState<AdminTab>("ai");

  // Memoize the deps object so its reference is stable across re-renders.
  // Without this, every render creates a fresh object → all admin hooks see a
  // new `deps` dependency → their useEffect re-fires → infinite fetch loop.
  // NOTE: useMemo must appear BEFORE the early return to satisfy Rules of Hooks.
  const deps: AdminHooksDeps = useMemo(
    () => ({ workerUrl, getIdToken }),
    [workerUrl, getIdToken],
  );

  if (!isAdmin) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="rounded-lg border border-red-900/50 bg-red-950/20 px-4 py-8 text-center"
      >
        <p className="text-sm font-medium text-red-400">
          Acceso denegado. Esta sección requiere permisos de administrador.
        </p>
        <p className="mt-1 text-xs text-zinc-500">
          La autorización definitiva se verifica en el servidor.
        </p>
      </div>
    );
  }

  return (
    <div aria-label="admin-panel" className="space-y-5 rounded-lg border border-zinc-800 p-4">
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-blue-400/10 px-2 py-0.5 text-xs font-medium text-blue-400">
          Admin
        </span>
        <span className="text-xs text-zinc-500">AutoDieta IA — Panel de supervisión</span>
      </div>
      <div
        role="tablist"
        aria-label="admin-tabs"
        className="flex gap-1 overflow-x-auto border-b border-zinc-800"
      >
        {TABS.map((t) => {
          const isActive = tab === t.id;
          const panelId = `admin-panel-${t.id}`;
          const tabId = `admin-tab-btn-${t.id}`;
          return (
            <button
              key={t.id}
              id={tabId}
              type="button"
              role="tab"
              data-testid={`admin-tab-${t.id}`}
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setTab(t.id)}
              className={`-mb-px shrink-0 border-b-2 px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                isActive
                  ? "border-blue-400 text-blue-400"
                  : "border-transparent text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`admin-panel-${tab}`}
        aria-labelledby={`admin-tab-btn-${tab}`}
      >
        {tab === "ai" ? (
          <ErrorBoundary>
            <AIPanel deps={deps} />
          </ErrorBoundary>
        ) : null}
        {tab === "users" ? (
          <ErrorBoundary>
            <UsersPanel deps={deps} />
          </ErrorBoundary>
        ) : null}
        {tab === "audit" ? (
          <ErrorBoundary>
            <AuditPanel deps={deps} />
          </ErrorBoundary>
        ) : null}
        {tab === "knowledge" ? (
          <ErrorBoundary>
            <KnowledgePanel deps={deps} />
          </ErrorBoundary>
        ) : null}
      </div>
    </div>
  );
}
